import { useAuth0 } from "@auth0/auth0-react";
import React, { useState, useEffect } from "react";
import { useToaster } from "rsuite";
import styled from "styled-components";
import axiosInstance from "../../../../../appHelpers/axiosInstance";
import PlusIcon from "@rsuite/icons/Plus";
import SettingHorizontalIcon from "@rsuite/icons/SettingHorizontal";
import Card from "../../../../ui/Card/Card";
import Badge from "../../../../ui/Badge/Badge";
import Button from "../../../../ui/Button/Button";
import SectionRibbon from "../../../../ui/SectionRibbon/SectionRibbon";
import NoDataState from "../../../../ui/NoDataState/NoDataState";
import Loader from "../../../../ui/Loader/Loader";
import { colors } from "../../../../ui/theme/colors";

export const CustomerAcumaticaFC = (props) => {
  const { getAccessTokenSilently } = useAuth0();
  const toaster = useToaster();
  const [acumaticaSites, setAcumaticaSites] = useState([]);
  const [acumaticaSystems, setAcumaticaSystems] = useState([]);
  const [loadingAcumaticaData, setLoadingAcumaticaData] = useState(true);
  const [loadingOperations, setLoadingOperations] = useState({});

  const getCustomerAcumaticaData = async () => {
    setLoadingAcumaticaData(true);
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const res = await axiosInstance({
        url: `/customer/acumatica/${props.customer}`,
        data: ``,
        headers: { authorization: `Bearer ${token}` },
      });

      setAcumaticaSites(res?.data[0]);
      setAcumaticaSystems(res?.data[1]);
    } catch (error) {
      console.error(error);
    }
    setLoadingAcumaticaData(false);
  };

  const handleAddSite = async (id) => {
    setLoadingOperations((prev) => ({ ...prev, [`site-${id}`]: true }));
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      await axiosInstance({
        method: "POST",
        url: `/customer/insert/site/`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        data: JSON.stringify({
          customer_id: props.customer,
          id: id,
        }),
      });

      toaster.push(siteSuccessMessage, "topCenter");
      setAcumaticaSites(
        acumaticaSites.filter((site) => {
          return site.id !== id;
        }),
      );
    } catch (error) {
      console.error(error);
      toaster.push(errorMessage, "topCenter");
    } finally {
      setLoadingOperations((prev) => ({ ...prev, [`site-${id}`]: false }));
    }
  };

  const handleAddSystem = async (id) => {
    setLoadingOperations((prev) => ({ ...prev, [`system-${id}`]: true }));
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      await axiosInstance({
        method: "POST",
        url: `/customer/insert/system/`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        data: JSON.stringify({
          customer_id: props.customer,
          id: id,
        }),
      });

      toaster.push(systemSuccessMessage, "topCenter");
      setAcumaticaSystems(
        acumaticaSystems.filter((system) => {
          return system.id !== id;
        }),
      );
    } catch (error) {
      console.error(error);
      toaster.push(errorMessage, "topCenter");
    } finally {
      setLoadingOperations((prev) => ({ ...prev, [`system-${id}`]: false }));
    }
  };

  useEffect(() => {
    getCustomerAcumaticaData();
  }, [props.customer]);

  if (loadingAcumaticaData) {
    return (
      <Container>
        <Loader content="Loading Acumatica data..." centered variant="glass" />
      </Container>
    );
  }

  return (
    <Container>
      <SectionRibbon
        title="Acumatica Integration"
        icon={SettingHorizontalIcon}
        variant="primary"
        rightContent={
          <StatsContainer>
            <StatItem>
              <StatLabel>New Systems:</StatLabel>
              <StatValue>{acumaticaSystems?.length || 0}</StatValue>
            </StatItem>
            <StatItem>
              <StatLabel>New Sites:</StatLabel>
              <StatValue>{acumaticaSites?.length || 0}</StatValue>
            </StatItem>
          </StatsContainer>
        }
      />

      <SystemsSection>
        <SectionLabel>
          <Badge variant="warning" size="small">
            NEW SYSTEMS
          </Badge>
        </SectionLabel>

        {acumaticaSystems.length > 0 ? (
          <SystemsGrid>
            {acumaticaSystems.map((system, index) => (
              <Card
                key={system.id}
                animated
                visible={true}
                delay={index * 50}
                hoverable
              >
                <SystemContent>
                  <SystemHeader>
                    <SystemId>#{system.id}</SystemId>
                    <AddButton
                      variant="success"
                      size="small"
                      onClick={() => handleAddSystem(system.id)}
                      loading={loadingOperations[`system-${system.id}`]}
                      disabled={loadingOperations[`system-${system.id}`]}
                    >
                      <PlusIcon
                        style={{ marginRight: "6px", fontSize: "14px" }}
                      />
                      Add System
                    </AddButton>
                  </SystemHeader>

                  <SystemTitle>
                    {[system.manufacturer, system.model]
                      .filter(Boolean)
                      .join(" ") || "Unknown System"}
                  </SystemTitle>

                  <SystemDetails>
                    <DetailRow>
                      <DetailLabel>CUID:</DetailLabel>
                      <DetailValue>{system.cus_sys_id || "N/A"}</DetailValue>
                    </DetailRow>

                    <DetailRow>
                      <DetailLabel>Modality:</DetailLabel>
                      <DetailValue>{system.modality || "N/A"}</DetailValue>
                    </DetailRow>

                    <DetailRow>
                      <DetailLabel>Serial Number:</DetailLabel>
                      <DetailValue>{system.serial_number || "N/A"}</DetailValue>
                    </DetailRow>

                    <DetailRow>
                      <DetailLabel>Room:</DetailLabel>
                      <DetailValue>{system.room || "N/A"}</DetailValue>
                    </DetailRow>
                  </SystemDetails>
                </SystemContent>
              </Card>
            ))}
          </SystemsGrid>
        ) : (
          <NoDataState
            icon="⚙️"
            title="No New Systems"
            message="No new systems available from Acumatica integration."
          />
        )}
      </SystemsSection>

      <SitesSection>
        <SectionLabel>
          <Badge variant="info" size="small">
            NEW SITES
          </Badge>
        </SectionLabel>

        {acumaticaSites.length > 0 ? (
          <SitesGrid>
            {acumaticaSites.map((site, index) => (
              <Card
                key={site.id}
                animated
                visible={true}
                delay={index * 50}
                hoverable
              >
                <SiteContent>
                  <SiteHeader>
                    <SiteId>#{site.id}</SiteId>
                  </SiteHeader>

                  <SiteName>{site.name || "Unnamed Site"}</SiteName>

                  <SiteDetails>
                    <DetailRow>
                      <DetailLabel>Location:</DetailLabel>
                      <DetailValue>
                        {[site.city, site.state].filter(Boolean).join(", ") ||
                          "N/A"}
                      </DetailValue>
                    </DetailRow>

                    <DetailRow>
                      <DetailLabel>Address:</DetailLabel>
                      <DetailValue>{site.street_address || "N/A"}</DetailValue>
                    </DetailRow>

                    <DetailRow>
                      <DetailLabel>ZIP:</DetailLabel>
                      <DetailValue>{site.zip || "N/A"}</DetailValue>
                    </DetailRow>
                  </SiteDetails>
                </SiteContent>
              </Card>
            ))}
          </SitesGrid>
        ) : (
          <NoDataState
            icon="🏢"
            title="No New Sites"
            message="No new sites available from Acumatica integration."
          />
        )}
      </SitesSection>
    </Container>
  );
};

const Container = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
`;

const StatsContainer = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
    align-items: flex-end;
  }
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatLabel = styled.span`
  font-size: 14px;
  color: ${colors.neutral[600]};
  font-weight: 500;
`;

const StatValue = styled.span`
  font-size: 16px;
  color: ${colors.neutral[800]};
  font-weight: 600;
`;

const SitesSection = styled.div`
  margin-bottom: 40px;
`;

const SystemsSection = styled.div`
  margin-bottom: 40px;
`;

const SectionLabel = styled.div`
  margin-bottom: 16px;
  display: flex;
  align-items: center;
`;

const SitesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SystemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SiteContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SystemContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SiteHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SystemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SiteId = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.neutral[600]};
`;

const SystemId = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.neutral[600]};
`;

const AddButton = styled(Button)`
  flex-shrink: 0;
  min-width: 100px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const SiteName = styled.h4`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${colors.neutral[800]};
  line-height: 1.4;
`;

const SystemTitle = styled.h4`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${colors.neutral[800]};
  line-height: 1.4;
`;

const SiteDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SystemDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const DetailRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const DetailLabel = styled.span`
  font-size: 13px;
  color: ${colors.neutral[500]};
  font-weight: 500;
  min-width: 70px;
  flex-shrink: 0;
`;

const DetailValue = styled.span`
  font-size: 13px;
  color: ${colors.neutral[700]};
  line-height: 1.4;
`;

const siteSuccessMessage = {
  type: "success",
  header: "Success",
  description: "Site added successfully!",
};

const systemSuccessMessage = {
  type: "success",
  header: "Success",
  description: "System added successfully!",
};

const errorMessage = {
  type: "error",
  header: "Error",
  description: "Something went wrong. Please try again.",
};
